import { type CreateStoredViewInput, type CreateStoredViewMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createStoredViewMutationDocument } from "./stored-view.gql";
import { EVENT_STORED_VIEW_CREATED, trackStoredViewEvent } from "./tracking";

export type CreatedStoredView = CreateStoredViewMutation["createStoredView"];

export interface UseCreateStoredViewProps {
  onCompleted?: (data: CreatedStoredView) => void;
  onError?: (e: Error) => void;
}

export const useCreateStoredView = ({ onCompleted, onError }: UseCreateStoredViewProps = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: CreateStoredViewInput) => {
      const client = createOperationClient("CreateStoredView");
      const data = await client.request(createStoredViewMutationDocument, { input });
      return data.createStoredView;
    },
    onSuccess: (newStoredView) => {
      trackStoredViewEvent(EVENT_STORED_VIEW_CREATED, newStoredView);

      // Invalidate stored view list to refetch with new data
      queryClient.invalidateQueries({
        queryKey: queryKeys.storedViews.list(newStoredView.key, newStoredView.workspaceId),
      });

      // If this is a default view, invalidate all stored view lists to ensure default states are correct
      if (newStoredView.default) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.storedViews.lists(),
        });
      }

      // Call the onCompleted callback
      if (onCompleted) {
        onCompleted(newStoredView);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    createStoredView: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
